import React, { useState, useEffect, useCallback } from "react";
import { Breadcrumb, Select, Table, Tabs, Popover, Spin, Modal } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import images from "../../themes/appImage";
import appconstant from "../../themes/appconstant";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { EllipsisOutlined } from "@ant-design/icons";
import {
  profileListLoad,
  proProfileLoad,
  videoDeleteLoad,
} from "../../redux/slices/managePros.slice";
import { imageURL } from "../../redux/Api/apikit";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { debounce } from "../../utils/global-func";
import InfiniteScroll from "react-infinite-scroll-component";
import QuestionBox from "../../common/appointementQuestion";

const { TabPane } = Tabs;
const { confirm } = Modal;

const ProfileDetail = () => {
  dayjs.extend(relativeTime);
  const navigate = useNavigate();
  const { proId } = useParams();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [payload, setPayload] = useState({
    offset: 0,
    limit: 10,
    order: 1,
    search: "",
    type: "session",
  });

  const {
    proProfile,
    proProfileLoading,
    profileList,
    isLoading,
    profilePagination,
  } = useSelector((state) => state.proReducer);

  // Fetch profile data
  useEffect(() => {
    if (proId) {
      dispatch(proProfileLoad({ proId }));
    }
  }, [proId, dispatch]);

  // Fetch list data based on payload
  useEffect(() => {
    dispatch(profileListLoad({ ...payload, offset: 0, proId }));
  }, [payload, proId, dispatch]);

  // Set document title
  useEffect(() => {
    document.title = "MentalFit";
  }, []);

  const fetchMoreData = () => {
    setPayload((prev) => ({ ...prev, offset: prev.offset + 10 }));
  };

  const onChangePageSize = (limit) => {
    setPayload((prev) => ({ ...prev, limit: Number(limit), offset: 0 }));
    setCurrentPage(1);
  };

  const onChangePage = (page) => {
    if (
      page < 1 ||
      (profilePagination &&
        page > Math.ceil(profilePagination.totalCount / payload.limit))
    ) {
      return;
    }
    setCurrentPage(page);
    setPayload((prev) => ({ ...prev, offset: (page - 1) * prev.limit }));
  };

  // Consolidated column definitions
  const getColumns = useCallback((type) => {
    const commonColumns = [
      {
        title: "ID",
        dataIndex: "srno",
        key: "srno",
        render: (_, __, index) => index + 1,
      },
    ];

    const sessionColumns = [
      {
        title: () => (
          <div className="sort-icons">
            <span>Participant</span>
            <img src={images.arrowLeft} alt="sort" />
          </div>
        ),
        dataIndex: "participant",
        key: "participant",
        sortDirections: ["ascend", "descend"],
        render: (_, record) => (
          <div className="grid grid-cols-[1fr_5fr]">
            <img
              src={images.maleTable}
              alt="user"
              className="w-[20px] h-[20px] rounded-full object-cover"
            />
            <p className="whitespace-nowrap !mb-0">
              {record?.user?.firstName + " " + record?.user?.lastName}
            </p>
          </div>
        ),
      },
      {
        title: "Username",
        dataIndex: "users",
        key: "users",
        render: (_, record) => <>{record?.userName}</>,
      },
      {
        title: "Date & Time",
        dataIndex: "date",
        key: "date",
        render: (_, record) => {
          const formattedDate = new Date(record?.date).toLocaleDateString(
            "en-US",
            {
              year: "numeric",
              month: "long",
              day: "numeric",
            }
          );
          const fromTime = new Date(record?.startTime).toLocaleTimeString(
            "en-US",
            {
              hour: "2-digit",
              minute: "2-digit",
              timeZone: "UTC",
            }
          );
          const toTime = new Date(record?.endTime).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "UTC",
          });
          return (
            <div className="table-tb-plan">
              {formattedDate}
              <p>
                {fromTime}-{toTime}
              </p>
            </div>
          );
        },
      },
      {
        title: "Ratings",
        dataIndex: "rating",
        key: "rating",
        render: (rating) => (
          <div className="organization-image-sec remove-the-gap">
            <span>
              <img src={images.starFill} alt="star" />
            </span>
            {rating || 0}
          </div>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status) => (
          <div className="status-active capitalize">{status}</div>
        ),
      },
      {
        title: "Earned",
        dataIndex: "price",
        key: "price",
        render: (price) => <div>${price}</div>,
      },
    ];

    const transactionColumns = [
      {
        title: () => (
          <div className="sort-icons">
            <span>Date</span>
            <img src={images.arrowLeft} alt="sort" />
          </div>
        ),
        dataIndex: "createdAt",
        key: "date",
        sortDirections: ["ascend", "descend"],
        render: (value) => {
          const date = new Date(value);
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
        },
      },
      {
        title: () => (
          <div className="sort-icons">
            <span>Activity</span>
            <img src={images.arrowLeft} alt="sort" />
          </div>
        ),
        dataIndex: "status",
        key: "status",
        sortDirections: ["ascend", "descend"],
        render: (activity) => (
          <div className="organization-image-sec">
            {activity}
            <span>
              <img src={images.uploadIcon} alt="upload" />
            </span>
          </div>
        ),
      },
      {
        title: "Amount",
        dataIndex: "price",
        key: "amount",
        render: (price) => <div>${price}</div>,
      },
    ];

    return type === "sessions"
      ? [...commonColumns, ...sessionColumns]
      : [...commonColumns, ...transactionColumns];
  }, []);

  const TableFooter = () => {
    const totalCount = profilePagination?.totalCount || 0;
    const from = totalCount === 0 ? 0 : (currentPage - 1) * payload.limit + 1;
    const to = Math.min(currentPage * payload.limit, totalCount);
    const totalPages = Math.ceil(totalCount / payload.limit);

    return (
      <div className="footer-section">
        <div className="foot-main-div">
          <div className="rows">
            <div className="row-per-page">
              <p>Rows per page</p>
            </div>
            <div className="top-checkbox fot-select">
              <Select
                value={String(payload.limit)}
                onChange={onChangePageSize}
                options={[
                  { value: "10", label: "10" },
                  { value: "15", label: "15" },
                  { value: "20", label: "20" },
                  { value: "25", label: "25" },
                ]}
              />
            </div>
          </div>
          <div className="page-count">
            <p>
              {from}-{to} of {totalCount}
            </p>
          </div>
          <div className="back-and-next">
            <span
              onClick={() => onChangePage(1)}
              style={{
                cursor: currentPage > 1 ? "pointer" : "not-allowed",
                opacity: currentPage > 1 ? 1 : 0.5,
              }}
            >
              <img src={images.next} className="rotate-180" alt="back" />
            </span>
            <span
              onClick={() => onChangePage(currentPage - 1)}
              style={{
                cursor: currentPage > 1 ? "pointer" : "not-allowed",
                opacity: currentPage > 1 ? 1 : 0.5,
              }}
            >
              <img src={images.new} className="rotate-180" alt="previous" />
            </span>
            <span
              onClick={() => onChangePage(currentPage + 1)}
              style={{
                cursor: currentPage < totalPages ? "pointer" : "not-allowed",
                opacity: currentPage < totalPages ? 1 : 0.5,
              }}
            >
              <img src={images.new} alt="next" />
            </span>
            <span
              onClick={() => onChangePage(totalPages)}
              style={{
                cursor: currentPage < totalPages ? "pointer" : "not-allowed",
                opacity: currentPage < totalPages ? 1 : 0.5,
              }}
            >
              <img src={images.next} alt="last" />
            </span>
          </div>
        </div>
      </div>
    );
  };

  const UserPost = ({ username, avatar, createdAt, text }) => {
    const [popoverOpen, setPopoverOpen] = useState(false);

    return (
      <div className="user-post remove-the-sp">
        <div className="post-rmover-setting">
          <div className="post-disp">
            <div className="h-[50px] w-[50px] rounded-full overflow-hidden">
              <img src={avatar} className="h-full object-cover" alt="avatar" />
            </div>
            <div className="user-name">
              <h2>{username}</h2>
              <p>{createdAt}</p>
            </div>
          </div>
          <div className="update-btn-right padding-right">
            <Popover
              content={
                <div className="popup" style={{ width: "200px" }}>
                  <div className="edit-account">
                    <p>
                      <span>
                        <img src={images.crossSquar} alt="lineDev" />
                      </span>
                      Remove Post
                    </p>
                  </div>
                </div>
              }
              title=""
              trigger="click"
              placement="bottomRight"
              overlayClassName="update-popup"
              open={popoverOpen}
              onOpenChange={setPopoverOpen}
            >
              <span
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === "Enter" && setPopoverOpen(true)}
              >
                <img src={images.dotesReact} alt="horizontalDots" />
              </span>
            </Popover>
          </div>
        </div>
        <div className="user-disp-text john-profile">
          <p>{text}</p>
        </div>
        <div className="post-image">
          <img src={images.post} alt="firstIcon" />
        </div>
      </div>
    );
  };

  const UserVideoPost = ({
    username,
    expertise,
    avatar,
    createdAt,
    text,
    videoSrc,
    id,
  }) => {
    const [popoverOpen, setPopoverOpen] = useState(false);

    const handleDelete = () => {
      setPopoverOpen(false);
      confirm({
        title: (
          <div style={{ textAlign: "center" }}>
            Are you sure you want to delete this post?
          </div>
        ),
        okText: "Yes",
        cancelText: "No",
        centered: true,
        onOk() {
          dispatch(
            videoDeleteLoad({
              post: id,
              onSuccess: () => {
                dispatch(
                  profileListLoad({
                    ...payload,
                    search: payload.search,
                    offset: 0,
                    proId,
                    type: "engageFeed",
                  })
                );
              },
            })
          );
        },
        onCancel() {},
        footer: (_, { OkBtn, CancelBtn }) => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px", // spacing between buttons
            }}
          >
            <CancelBtn />
            <OkBtn />
          </div>
        ),
      });
    };

    return (
      <div className="relative w-[40%] h-[80vh] bg-black text-white flex justify-center items-center overflow-hidden">
        <video
          src={videoSrc}
          autoPlay
          muted
          loop
          playsInline
          controls
          controlsList="nodownload"
          className="w-full h-full object-contain z-10"
          onError={(e) => console.error("Video error:", e)}
        >
          Your browser does not support the video tag.
        </video>
        <div className="absolute top-0 left-0 w-full h-full flex flex-col justify-between p-4 pointer-events-none z-20">
          <div className="flex items-center gap-3"></div>
          <div className="mb-6 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full overflow-hidden border border-white mt-2">
                <img
                  src={avatar}
                  className="h-full w-full object-cover"
                  alt="avatar"
                />
              </div>
              <h2 className="text-md text-gray-300 mt-5">{username}</h2>
              {/* <p className="text-sm text-gray-300 mt-11 ml-[-105px]">
                {expertise}
              </p> */}
            </div>
            <p className="text-sm text-white">{text}</p>
          </div>
          <div className="absolute top-4 right-4 pointer-events-auto">
            <Popover
              content={
                <div className="popup text-black" style={{ width: "200px" }}>
                  <div
                    className="edit-account cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                    onClick={handleDelete}
                  >
                    <p className="flex items-center gap-2">
                      <img src={images.deleteIcon} alt="Remove" />
                      Delete Post
                    </p>
                  </div>
                </div>
              }
              trigger="click"
              placement="bottomRight"
              overlayClassName="update-popup"
              open={popoverOpen}
              onOpenChange={setPopoverOpen}
            >
              <span
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === "Enter" && setPopoverOpen(true)}
              >
                {/* <img src={images.dotesReact} alt="options" /> */}

                <EllipsisOutlined
                  className="text-white"
                  style={{
                    fontSize: 20,
                    cursor: "pointer",
                    filter: "drop-shadow(0 0 1px rgba(11,31,15,0.9))",
                  }}
                />
              </span>
            </Popover>
          </div>
        </div>
      </div>
    );
  };

  const InnerCircle = ({ Description, title }) => (
    <div className="create-entertainment-circle">
      <div className="enter-flex-ps">
        <div className="enter-taain-heading">
          <h1>{title}</h1>
          <p>{Description}</p>
        </div>
      </div>
      <div className="reation-imoze">
        <div className="avatars">
          {profileList[0]?.totalUser?.slice(0, 5).map((user) => (
            <span key={user._id}>
              <img
                crossOrigin="*"
                src={`${imageURL + user.avatar}`}
                alt={user.userName}
              />
            </span>
          ))}
          {profileList?.totalUser?.length > 5 && (
            <span className="get-more">
              +{profileList.totalUser.length - 5}
            </span>
          )}
        </div>
      </div>
      <div className="engagement-rate">
        <div className="engage-text">
          <h5>Engagement Rate</h5>
        </div>
        <div className="per-rate">
          <span>90%</span>
        </div>
      </div>
    </div>
  );

  const EncouragementGroup = ({ image, title }) => (
    <div className="create-entertainment-circle">
      <div className="encouragement-groups">
        <div className="sunday-image">
          <img src={image} alt={title} crossOrigin="*" />
          <p>{title}</p>
        </div>
        <div className="follow-btn-ps"></div>
      </div>
    </div>
  );

  return (
    <div className="site-layout-background">
      <div className="top-header-organi-zations">
        <div className="organi-zations-heading-text">
          <h1>Manage Pros</h1>
          <Breadcrumb separator=">">
            <Breadcrumb.Item>
              <Link to="/managepros">Manage Pros</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{appconstant.profileDetail}</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <div className="user-main-section">
        <div className="user-profile-div">
          <div className="user-left-side-sec">
            <div className="inner-section-ps">
              <div className="relative h-[100px] mb-8 rounded-t-xl bg-[#F4CF67]">
                <img
                  src={images.proBanner}
                  alt="proBanner"
                  className="w-full"
                />
                <div className="absolute -bottom-[35px] left-1/2 transform -translate-x-1/2">
                  <div className="relative">
                    <img
                      src={images.badge}
                      className="absolute bottom-0 right-0"
                      alt="badge"
                    />
                    <div className="h-[70px] w-[70px] rounded-full overflow-hidden">
                      <img
                        src={images.maleTable}
                        alt="avatarWith"
                        className="w-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="avatar-name">
                <h2>{proProfile?.userName || "N/A"}</h2>
                <p>{`${proProfile?.firstName || ""} ${
                  proProfile?.lastName || ""
                }`}</p>
                <div className="avatar-ranking pink-star">
                  {Array(5)
                    .fill()
                    .map((_, i) => (
                      <img key={i} src={images.pinkStar} alt="pinkStar" />
                    ))}
                  <p>{proProfile?.rating?.toFixed(1) || "0.0"}</p>
                </div>
              </div>

              <div className="followers-section">
                <div className="avatar-post-numbers direct_care_report-flex">
                  {[
                    { value: "40", label: "Followers" },
                    {
                      value: `${proProfile?.experience || "0"} yr`,
                      label: "Experience",
                    },
                    {
                      value: proProfile?.totalPost?.toString() || "0",
                      label: "Posts",
                    },
                    {
                      value: proProfile?.totalSession?.toString() || "0",
                      label: "Sessions",
                    },
                  ].map(({ value, label }) => (
                    <div key={label} className="following-div border-line">
                      <p>{value}</p>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="work-on-most">
                {[
                  {
                    title: "Primary Expertise",
                    content:
                      proProfile?.primaryExpertise
                        ?.map((e) => e.name)
                        .join(", ") || "N/A",
                  },
                  {
                    title: "Secondary Expertise",
                    content:
                      proProfile?.secondaryExpertise
                        ?.map((e) => e.name)
                        .join(", ") || "N/A",
                  },
                  {
                    title: "Bio",
                    content: proProfile?.bio || "N/A",
                    extra:
                      proProfile?.bio?.length > 50 ? (
                        <span>see more</span>
                      ) : null,
                  },
                ].map(({ title, content, extra }) => (
                  <div key={title} className="primary-expertise-text-bold">
                    <h2>{title}</h2>
                    <p>
                      {content} {extra}
                    </p>
                  </div>
                ))}

                <div className="top-mt">
                  {[
                    {
                      label: "Coins",
                      value: proProfile?.coin?.toString() || "0",
                      icon: images.coins,
                    },
                    { label: "Email", value: proProfile?.email || "N/A" },
                    {
                      label: "Gender",
                      value: proProfile?.identityType || "N/A",
                    },
                    {
                      label: "DOB",
                      value: proProfile?.dob
                        ? new Date(proProfile.dob).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                        : "N/A",
                    },
                    {
                      label: "Visibility",
                      value: proProfile?.visibility ? "ON" : "OFF",
                    },
                    {
                      label: "WHO-5 Wellness Index",
                      value: proProfile?.whoIndex || "N/A",
                    },
                    { label: "Streak", value: proProfile?.streak || "N/A" },
                  ].map(({ label, value, icon }) => (
                    <div key={label} className="coins">
                      <p>{label}</p>
                      <span className={label === "Email" ? "" : "capitalize"}>
                        {icon && <img src={icon} alt={label} />} {value}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="profile-freeze">
                  <button>Freeze Profile</button>
                  <button>Remove Profile</button>
                </div>
              </div>

              <div className="bottom-text">
                <p>
                  Member Since:
                  {proProfile?.createdAt
                    ? new Date(proProfile.createdAt).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="post-page-section">
            <div className="appointment-rates-sections">
              <div className="left-side-appointment">
                <h2>Appointment Rates</h2>
                <div className="appoint-ment-grid-div">
                  {[
                    { time: "15 min", multiplier: 0.25 },
                    { time: "30 min", multiplier: 0.5 },
                    { time: "45 min", multiplier: 0.75 },
                    { time: "1 hour", multiplier: 1 },
                  ].map(({ time, multiplier }) => {
                    const rate = proProfile?.hourlyRate || 0;
                    const price = `$${(rate * multiplier).toFixed(2)}`;
                    return (
                      <div key={time} className="min-box box-shadow-none">
                        <p>{time}</p>
                        <h1>{price}</h1>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="left-side-appointment flex flex-col justify-between h-[143px] right-side-widht-set">
                <h2>Earnings</h2>
                {[
                  {
                    label: "Available Earnings",
                    value: `$${proProfile?.totalEarning || "N/A"}`,
                  },
                ].map(({ label, value }) => (
                  <div key={label} className="amount-withdrawn">
                    <p>{label}</p>
                    <span>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="slider-tab-div slider-section profile-page-tab">
              <Tabs
                className="mang"
                onChange={(key) => {
                  setPayload((prev) => ({
                    ...prev,
                    type:
                      key === "1"
                        ? "session"
                        : key === "2"
                        ? "earning"
                        : key === "8"
                        ? "engageFeed"
                        : key === "3"
                        ? "post"
                        : key === "4"
                        ? "innerCircle"
                        : key === "5"
                        ? "encouragement"
                        : "questions",
                    offset: 0,
                    search: "",
                  }));
                  setSearchValue("");
                }}
              >
                <TabPane tab="Sessions" key="1">
                  <div className="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 system-line">
                    <input
                      type="text"
                      className="iocn-search search-bar"
                      placeholder="Search"
                      value={searchValue}
                      onChange={(e) => {
                        setSearchValue(e.target.value.trimStart());
                        debounce(
                          () =>
                            setPayload({ ...payload, search: e.target.value }),
                          400
                        )();
                      }}
                    />
                    <div className="top-checkbox">
                      <Select
                        defaultValue="Filter"
                        onChange={(value) => console.log(`selected ${value}`)}
                        options={[
                          { value: "one", label: "1 months" },
                          { value: "two", label: "3 months" },
                          { value: "three", label: "6 months" },
                        ]}
                      />
                    </div>
                  </div>
                  {isLoading ? (
                    <Spin className="spin-table" />
                  ) : (
                    <Table
                      dataSource={profileList}
                      columns={getColumns("sessions")}
                      showSorterTooltip={false}
                      pagination={false}
                    />
                  )}
                  <TableFooter />
                </TabPane>

                <TabPane tab="Transactions History" key="2">
                  <div className="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 system-line">
                    <input
                      type="text"
                      className="iocn-search search-bar"
                      placeholder="Search"
                      value={searchValue}
                      onChange={(e) => {
                        setSearchValue(e.target.value.trimStart());
                        debounce(
                          () =>
                            setPayload({ ...payload, search: e.target.value }),
                          400
                        )();
                      }}
                    />
                    <div className="top-checkbox">
                      <Select
                        defaultValue="Filter"
                        onChange={(value) => console.log(`selected ${value}`)}
                        options={[
                          { value: "one", label: "1 months" },
                          { value: "two", label: "3 months" },
                          { value: "three", label: "6 months" },
                        ]}
                      />
                    </div>
                  </div>
                  {isLoading ? (
                    <Spin className="spin-table" />
                  ) : (
                    <Table
                      dataSource={profileList}
                      columns={getColumns("transactions")}
                      showSorterTooltip={false}
                      pagination={false}
                    />
                  )}
                  <TableFooter />
                </TabPane>

                {/* <TabPane tab="Engage Feed" key="8">
                  {isLoading ? (
                    <Spin className="spin-table" />
                  ) : (
                    <div
                      className="engageFeed-scroll-container"
                      style={{
                        height: "calc(100vh - 150px)",
                        overflowY: "scroll",
                        scrollSnapType: "y mandatory",
                      }}
                    >
                      {profileList?.map((engageFeed) => (
                        <div
                          key={engageFeed._id}
                          style={{
                            height: "100vh",
                            scrollSnapAlign: "start",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: "white",
                          }}
                        >
                          <UserVideoPost
                            id={engageFeed._id}
                            username={engageFeed.userData?.userName}
                            // expertise="Fitness Coach"
                            avatar={`${imageURL + engageFeed.userData?.avatar}`}
                            createdAt={new Date(
                              engageFeed.createdAt
                            ).toLocaleDateString()}
                            text={engageFeed.text || ""}
                            videoSrc={`${imageURL}${engageFeed.file}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </TabPane> */}
                <TabPane tab="Engage Feed" key="8">
                  {isLoading ? (
                    <Spin className="spin-table" />
                  ) : profileList?.length > 0 ? (
                    <div
                      className="engageFeed-scroll-container"
                      style={{
                        height: "calc(100vh - 150px)",
                        overflowY: "scroll",
                        scrollSnapType: "y mandatory",
                      }}
                    >
                      {profileList?.map((engageFeed) => (
                        <div
                          key={engageFeed._id}
                          style={{
                            height: "100vh",
                            scrollSnapAlign: "start",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: "white",
                          }}
                        >
                          <UserVideoPost
                            id={engageFeed._id}
                            username={engageFeed.userData?.userName}
                            avatar={`${imageURL + engageFeed.userData?.avatar}`}
                            createdAt={new Date(
                              engageFeed.createdAt
                            ).toLocaleDateString()}
                            text={engageFeed.text || ""}
                            videoSrc={`${imageURL}${engageFeed.file}`}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        height: "calc(100vh - 150px)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "18px",
                        color: "#888",
                      }}
                    >
                      No Data Found
                    </div>
                  )}
                </TabPane>
                {/* <TabPane tab="Posts" key="3">
                  {isLoading ? (
                    <Spin className="spin-table" />
                  ) : (
                    <InfiniteScroll
                      className="post-page-width !h-[calc(100vh-319px)] overflow-auto"
                      dataLength={profileList?.length}
                      next={fetchMoreData}
                      hasMore={
                        profileList?.length > 0 &&
                        profileList?.length <
                          (profilePagination?.totalCount || 0)
                      }
                      loader={
                        <Spin className="relative left-1/2 transform -translate-x-1/2 mt-1" />
                      }
                    >
                      {profileList?.map((post) => (
                        <UserPost
                          key={post._id}
                          username={post.userData?.userName}
                          avatar={`${imageURL + post.userData?.avatar}`}
                          createdAt={dayjs(post.createdAt).fromNow()}
                          text={post.text}
                        />
                      ))}
                    </InfiniteScroll>
                  )}
                </TabPane> */}
                <TabPane tab="Posts" key="3">
                  {isLoading ? (
                    <Spin className="spin-table" />
                  ) : profileList?.length === 0 ? (
                    <div className="text-center mt-4 text-gray-500">
                      No data found
                    </div>
                  ) : (
                    <InfiniteScroll
                      className="post-page-width !h-[calc(100vh-319px)] overflow-auto"
                      dataLength={profileList?.length}
                      next={fetchMoreData}
                      hasMore={
                        profileList?.length > 0 &&
                        profileList?.length <
                          (profilePagination?.totalCount || 0)
                      }
                      loader={
                        <Spin className="relative left-1/2 transform -translate-x-1/2 mt-1" />
                      }
                    >
                      {profileList?.map((post) => (
                        <UserPost
                          key={post._id}
                          username={post.userData?.userName}
                          avatar={`${imageURL + post.userData?.avatar}`}
                          createdAt={dayjs(post.createdAt).fromNow()}
                          text={post.text}
                        />
                      ))}
                    </InfiniteScroll>
                  )}
                </TabPane>

                <TabPane tab="Inner circles" key="4">
                  {isLoading ? (
                    <Spin className="spin-table" />
                  ) : (
                    <div className="inner-circle top-padding-none">
                      <div className="create-inner-section top-space-group">
                        {profileList.map((item, i) => (
                          <InnerCircle
                            key={item._id || i}
                            title={item?.circleDetail?.name || "N/A"}
                            Description={
                              item?.circleDetail?.description || "N/A"
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </TabPane>

                <TabPane tab="Encouragement Groups" key="5">
                  {isLoading ? (
                    <Spin className="spin-table" />
                  ) : (
                    <div className="inner-circle top-padding-none">
                      <div className="create-inner-section encouragement-section-grid top-space-group">
                        {profileList.map((item, i) => (
                          <EncouragementGroup
                            key={item._id || i}
                            image={`${imageURL + item?.groupDetail?.image}`}
                            title={item?.groupDetail?.name}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </TabPane>

                <TabPane
                  tab={`Appointment Questions(${profileList.length})`}
                  key="6"
                >
                  <div className="grid gap-5 p-5">
                    {profileList.map((item, i) => (
                      <QuestionBox
                        key={item._id || i}
                        questionNumber={`0${i + 1}`}
                        questionTitle={item?.question}
                        inputType={item?.type}
                        options={item?.answers?.map((a) => ({
                          value: a._id,
                          label: a.ans,
                        }))}
                        showTextarea={false}
                      />
                    ))}
                  </div>
                </TabPane>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileDetail;
